// lib/clients/helpers.ts
// Shared utilities that derive values from a ClientConfig.
// Used by API routes, search logic, and UI components.

import type { ClientConfig, AppliedFilters } from './types';

/**
 * Build the full Meilisearch facets array from a client config.
 * Universal facets (brand, category, price) + client-specific facet fields.
 */
export function getFacetList(config: Pick<ClientConfig, 'facetFields'>): string[] {
  return ['brand', 'category', 'price', ...config.facetFields.map(f => f.field)];
}

/**
 * Build the Meilisearch filterableAttributes array from a client config.
 * Every facet must be filterable, plus `model` (SKU) so specific products can
 * be pinned/looked up by code (e.g. ui.popularProductCodes). `model` is NOT a
 * facet — it's filterable-only, no sidebar group. Declaring it for clients
 * whose docs lack a model is harmless (it simply never matches).
 */
export function getFilterableAttributes(
  config: Pick<ClientConfig, 'facetFields'> & Partial<Pick<ClientConfig, 'warehouseScope'>>,
): string[] {
  return Array.from(new Set([
    ...getFacetList(config),
    'model',
    // Warehouse scoping needs its field filterable but NOT faceted: the customer's
    // warehouse comes from their login, so it is never a sidebar choice. Declared
    // here rather than in a runbook step so a re-crawl (which rebuilds the index
    // from scratch) can never silently drop it — same reasoning as `id` for
    // external pricing in applyIndexSettings.
    // De-duplicated: acalight ALREADY has a facet literally named
    // `warehouses_available` (its user-facing «Διαθεσιμότητα Αποθήκης» sidebar
    // group, values = warehouse NAMES). A client could legitimately have both
    // that facet and a scope on the same field, and declaring it twice would be
    // sloppy at best.
    ...(config.warehouseScope?.field ? [config.warehouseScope.field] : []),
  ]));
}

/**
 * Are price filters (sidebar bands + typed "έως 50€" ranges) offered to this
 * client's shoppers?
 *
 * B2B clients get NONE — team decision 2026-07-28. This is deliberately DERIVED
 * from `features.externalPricing` rather than being its own per-client flag,
 * because it is not a preference: when prices are per-customer, the only price
 * we hold is the indexed RRP, which is NOT what that customer sees or pays.
 * Filtering on it returns confidently wrong products. Every attempt to bridge
 * that gap (browser-side recount, then the full price-map flow) failed on
 * either honesty or load, and the one-shot "all prices" endpoint that would
 * have fixed it was ruled out by the client. So the feature is not offered.
 *
 * Deriving it means a NEW B2B client is covered the moment external pricing is
 * switched on — there is no provisioning step to forget. B2C clients are
 * untouched: no external pricing → price filters exactly as before.
 *
 * Price DISPLAY is unaffected; this governs filtering only.
 */
export function priceFiltersEnabled(config: Pick<ClientConfig, 'features'>): boolean {
  return !config.features?.externalPricing;
}

/**
 * Build initial empty applied filters state from a client config.
 * Used to initialize filter state in SearchWidget and AgentChatWidget.
 */
export function createEmptyFilters(config: ClientConfig): AppliedFilters {
  const custom: Record<string, string[]> = {};
  for (const f of config.facetFields) {
    custom[f.field] = [];
  }
  return {
    categories: [],
    brands: [],
    priceRange: null,
    custom,
  };
}

/**
 * Check if any filters are currently applied.
 */
export function hasActiveFilters(filters: AppliedFilters): boolean {
  if (filters.categories.length > 0) return true;
  if (filters.brands.length > 0) return true;
  if (filters.priceRange) return true;
  for (const values of Object.values(filters.custom)) {
    if (values.length > 0) return true;
  }
  return false;
}

/**
 * Convert AppliedFilters to Meilisearch filter strings.
 * Returns an array of filter clauses to be joined with ' AND '.
 */
export function buildMeilisearchFilters(filters: AppliedFilters): string[] {
  const clauses: string[] = [];

  if (filters.categories.length > 0) {
    clauses.push(`category IN [${filters.categories.map(c => `"${c}"`).join(', ')}]`);
  }

  if (filters.brands.length > 0) {
    clauses.push(`brand IN [${filters.brands.map(b => `"${b}"`).join(', ')}]`);
  }

  if (filters.priceRange) {
    clauses.push(filters.priceRange);
  }

  // Dynamic facet field filters
  for (const [field, values] of Object.entries(filters.custom)) {
    if (values.length > 0) {
      clauses.push(`${field} IN [${values.map(v => `"${v}"`).join(', ')}]`);
    }
  }

  return clauses;
}

// ── Filter Group Types (for sidebar rendering) ──

export interface FilterGroupItem {
  name: string;
  count: number;
}

export interface FilterGroup {
  title: string;
  maxVisible: number;
  items: FilterGroupItem[];
}

/**
 * Build sidebar filter groups for custom facet fields from Meilisearch facetDistribution.
 * Universal groups (categories, brands, price) are handled separately by the widget.
 */
/** Whether a facet field is kept out of the filters sidebar (ui.hiddenFacets). */
export function isFacetHidden(config: Pick<ClientConfig, 'ui'>, field: string): boolean {
  return config.ui.hiddenFacets?.includes(field) ?? false;
}

export function buildCustomFilterGroups(
  facetDistribution: Record<string, Record<string, number>> | undefined,
  config: ClientConfig,
  maxVisible = 5,
): FilterGroup[] {
  if (!facetDistribution) return [];

  const groups: FilterGroup[] = [];
  for (const ff of config.facetFields) {
    // `category` / `subcategory` are rendered by the universal category UI
    // (Προτεινόμενες / Άλλες Κατηγορίες in SearchWidget). They live in
    // facetFields only to stay filterable in Meilisearch — skip them here so
    // they don't also surface as a duplicate generic facet group (e.g. the
    // stray "Κατηγορία" group on kaizershop). Mirrors AgentChatWidget.
    if (ff.field === 'category' || ff.field === 'subcategory') continue;
    if (isFacetHidden(config, ff.field)) continue;
    const facets = facetDistribution[ff.field];
    if (facets && Object.keys(facets).length > 0) {
      groups.push({
        title: ff.label,
        maxVisible,
        items: Object.entries(facets)
          .sort(([, a], [, b]) => b - a)
          .map(([name, count]) => ({ name, count })),
      });
    }
  }
  return groups;
}

/**
 * Look up a facet field config by its sidebar label.
 * Used in filter toggle handlers to map "Χρώμα" → { field: 'color', ... }
 */
export function findFacetByLabel(config: ClientConfig, label: string) {
  return config.facetFields.find(f => f.label === label) ?? null;
}

/**
 * Look up a facet field config by its Meilisearch field name.
 * Used in search/agent code to map 'color' → { label: 'Χρώμα', ... }
 */
export function findFacetByField(config: ClientConfig, fieldName: string) {
  return config.facetFields.find(f => f.field === fieldName) ?? null;
}
